import type { AnalyticsDay } from './todayData';

/**
 * Pure helpers for the website analytics screens (Content → Analytics,
 * Today): the chart's bars and the "updated …" wording. Kept apart from the
 * components so they can be tested without React.
 */

const shortDay = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });

/** Days into bars: one per day, or per week over 90 days (the newest week ends today). */
export function barsOf(daily: AnalyticsDay[], days: 7 | 28 | 90) {
  if (days !== 90) return daily.map((d) => ({ ...d, label: shortDay(d.day) }));
  const out: Array<AnalyticsDay & { label: string }> = [];
  for (let end = daily.length; end > 0; end -= 7) {
    const week = daily.slice(Math.max(0, end - 7), end);
    out.unshift({
      day: week[0].day,
      label: `Week of ${shortDay(week[0].day)}`,
      visitors: week.reduce((n, d) => n + d.visitors, 0),
      pageviews: week.reduce((n, d) => n + d.pageviews, 0),
      search: week.reduce((n, d) => n + d.search, 0),
      social: week.reduce((n, d) => n + d.social, 0),
      other: week.reduce((n, d) => n + d.other, 0),
    });
  }
  return out;
}

/** "updated 2:30 PM", "updated yesterday, 9:10 AM", "updated Oct 18". */
export function updatedWhen(iso: string | null, now = new Date()) {
  if (!iso) return '';
  const d = new Date(iso);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const days = Math.floor(
    (new Date(now.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86400000
  );
  if (days <= 0) return `updated ${time}`;
  if (days === 1) return `updated yesterday, ${time}`;
  return `updated ${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}`;
}

/** What to call a top page: its TinyCMS title, "Home page" for /, else its address. */
export const pageName = (p: { title: string | null; path: string }) =>
  p.title || (p.path === '/' || p.path === '' ? 'Home page' : p.path);
